import React, { useState, useEffect, useCallback } from 'react';
import { getAccessToken } from '@/lib/tokenStore';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  Loader2, Wrench, CloudRain, CheckCircle2, AlertTriangle, XCircle, Save,
  Plus, Pencil, Trash2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '@/components/ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const hdrs = () => {
  const t = getAccessToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const STATUS_CONFIG = {
  achterstallig: { label: 'Achterstallig', color: 'bg-red-500', badge: 'bg-red-100 text-red-700', icon: XCircle, iconColor: 'text-red-500' },
  aan_de_beurt: { label: 'Aan de beurt', color: 'bg-amber-500', badge: 'bg-amber-100 text-amber-700', icon: AlertTriangle, iconColor: 'text-amber-500' },
  recent: { label: 'Recent gedaan', color: 'bg-green-500', badge: 'bg-green-100 text-green-700', icon: CheckCircle2, iconColor: 'text-green-500' }
};

const fmtDagen = (d) => {
  if (d == null) return 'Nooit uitgevoerd';
  if (d === 0) return 'Vandaag';
  if (d === 1) return '1 dag geleden';
  return `${d} dagen geleden`;
};

const CYCLUS_LABELS = {
  dagelijks: 'Dagelijks',
  wekelijks: 'Wekelijks',
  maandelijks: 'Maandelijks',
  kwartaal: 'Kwartaal'
};

const fmtFreq = (taak) => {
  if (taak.cyclus && CYCLUS_LABELS[taak.cyclus]) return CYCLUS_LABELS[taak.cyclus];
  const d = taak.frequentie?.minimaalDagen || 30;
  if (d <= 1) return 'Dagelijks';
  if (d <= 7) return 'Wekelijks';
  if (d <= 30) return 'Maandelijks';
  if (d <= 90) return 'Kwartaal';
  return `1x per ${Math.round(d / 30)} maanden`;
};

// ── Uitvoeren Modal ─────────────────────────────────────────────────────────
const UitvoerenModal = ({ taak, locatieId, onClose, onSaved }) => {
  const [notities, setNotities] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/locaties/${locatieId}/rusttaken/${taak.taakId}/uitvoeren`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...hdrs() },
        body: JSON.stringify({ notities })
      });
      if (res.ok) {
        toast.success('Taak afgerond');
        onSaved?.();
        onClose();
      } else toast.error('Opslaan mislukt');
    } catch { toast.error('Opslaan mislukt'); }
    finally { setSaving(false); }
  };

  if (!taak) return null;

  return (
    <Dialog open={!!taak} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Rusttaak uitvoeren</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <span className="text-sm text-slate-500">Taak</span>
            <p className="font-medium text-slate-800">{taak.titel}</p>
          </div>
          <div>
            <span className="text-sm text-slate-500">Laatst uitgevoerd</span>
            <p className="text-slate-700">{fmtDagen(taak.dagenGeleden)}{taak.laatstDoor ? ` door ${taak.laatstDoor}` : ''}</p>
          </div>
          <div>
            <Label htmlFor="notities">Notities (optioneel)</Label>
            <Textarea id="notities" value={notities} onChange={(e) => setNotities(e.target.value)} rows={3} placeholder="Opmerkingen..." data-testid="uitvoeren-notities" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuleren</Button>
          <Button onClick={handleSubmit} disabled={saving} className="bg-[#1f2548] hover:bg-[#2a3060]" data-testid="uitvoeren-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Taak afronden
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ── Create/Edit Taak Modal ──────────────────────────────────────────────────
const TaakFormModal = ({ taak, onClose, onSaved }) => {
  const [form, setForm] = useState({
    titel: taak?.titel || '',
    beschrijving: taak?.beschrijving || '',
    cyclus: taak?.cyclus || 'maandelijks',
    rustmodus_only: taak?.rustmodus_only || false
  });
  const [saving, setSaving] = useState(false);
  const isEdit = !!taak;

  const handleSubmit = async () => {
    if (!form.titel.trim()) {
      toast.error('Naam is verplicht');
      return;
    }
    setSaving(true);
    try {
      const url = isEdit ? `${API}/rusttaken/${taak.taakId}` : `${API}/rusttaken`;
      const method = isEdit ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', ...hdrs() },
        body: JSON.stringify(form)
      });
      if (res.ok) {
        toast.success(isEdit ? 'Taak bijgewerkt' : 'Taak aangemaakt');
        onSaved?.();
        onClose();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Opslaan mislukt');
      }
    } catch {
      toast.error('Opslaan mislukt');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="taak-form-modal">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Rusttaak bewerken' : 'Nieuwe rusttaak'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label htmlFor="titel">Naam *</Label>
            <Input
              id="titel"
              value={form.titel}
              onChange={(e) => setForm(p => ({ ...p, titel: e.target.value }))}
              placeholder="Naam van de taak"
              data-testid="taak-titel-input"
            />
          </div>
          <div>
            <Label htmlFor="beschrijving">Beschrijving</Label>
            <Textarea
              id="beschrijving"
              value={form.beschrijving}
              onChange={(e) => setForm(p => ({ ...p, beschrijving: e.target.value }))}
              rows={3}
              placeholder="Optionele beschrijving..."
              data-testid="taak-beschrijving-input"
            />
          </div>
          <div>
            <Label>Cyclus</Label>
            <Select value={form.cyclus} onValueChange={(v) => setForm(p => ({ ...p, cyclus: v }))}>
              <SelectTrigger data-testid="taak-cyclus-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="dagelijks">Dagelijks</SelectItem>
                <SelectItem value="wekelijks">Wekelijks</SelectItem>
                <SelectItem value="maandelijks">Maandelijks</SelectItem>
                <SelectItem value="kwartaal">Kwartaal</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-3">
            <Switch
              id="rustmodus"
              checked={form.rustmodus_only}
              onCheckedChange={(v) => setForm(p => ({ ...p, rustmodus_only: v }))}
              data-testid="taak-rustmodus-switch"
            />
            <Label htmlFor="rustmodus" className="text-sm">Alleen uitvoeren tijdens rustige periodes</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuleren</Button>
          <Button onClick={handleSubmit} disabled={saving} className="bg-[#1f2548] hover:bg-[#2a3060]" data-testid="taak-form-submit">
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            {isEdit ? 'Opslaan' : 'Aanmaken'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ── Taak Card ───────────────────────────────────────────────────────────────
const TaakCard = ({ taak, onUitvoeren, onEdit, onDelete, magBeheren }) => {
  const cfg = STATUS_CONFIG[taak.status] || STATUS_CONFIG.recent;
  const Icon = cfg.icon;

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center justify-between hover:shadow-sm transition-all" data-testid={`rusttaak-card-${taak.taakId}`}>
      <div className="flex items-start gap-3 min-w-0">
        <Icon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${cfg.iconColor}`} />
        <div className="min-w-0">
          <p className="font-medium text-slate-700">{taak.titel}</p>
          {taak.beschrijving && <p className="text-xs text-slate-400 mt-0.5 line-clamp-1">{taak.beschrijving}</p>}
          <div className="flex items-center gap-3 mt-0.5 text-sm text-slate-500">
            <span>{fmtDagen(taak.dagenGeleden)}</span>
            <span className="text-slate-300">|</span>
            <span>{fmtFreq(taak)}</span>
            {taak.rustmodus_only && <Badge className="bg-blue-50 text-blue-600 text-[10px]">Rustmodus</Badge>}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0 ml-2">
        {magBeheren && (
          <>
            <Button size="icon" variant="ghost" onClick={() => onEdit(taak)} className="w-8 h-8 text-slate-400 hover:text-[#1f2548]" data-testid={`edit-btn-${taak.taakId}`}>
              <Pencil className="w-3.5 h-3.5" />
            </Button>
            <Button size="icon" variant="ghost" onClick={() => onDelete(taak)} className="w-8 h-8 text-slate-400 hover:text-red-500" data-testid={`delete-btn-${taak.taakId}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </>
        )}
        {taak.status !== 'recent' && (
          <Button size="sm" variant="outline" onClick={() => onUitvoeren(taak)} data-testid={`uitvoeren-btn-${taak.taakId}`}>
            Uitvoeren
          </Button>
        )}
      </div>
    </div>
  );
};

// ── Main Page ───────────────────────────────────────────────────────────────
const Rusttaken = () => {
  const { selectedLocatie, selectedLocatieData } = useOutletContext();
  const { isAdmin, canManage } = useAuth();
  // 6.27b: canManage is een functie — zonder aanroep is hij altijd waar
  const magBeheren = typeof canManage === 'function' ? canManage() : !!canManage;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uitvoerenTaak, setUitvoerenTaak] = useState(null);
  const [toggling, setToggling] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editTaak, setEditTaak] = useState(null);
  const [deleteTaak, setDeleteTaak] = useState(null);

  const fetchData = useCallback(async () => {
    if (!selectedLocatie) { setLoading(false); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/locaties/${selectedLocatie}/rusttaken`, { headers: hdrs() });
      if (res.ok) setData(await res.json());
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [selectedLocatie]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleToggleRustmodus = async () => {
    const newState = !data?.rustmodus?.actief;
    setToggling(true);
    try {
      const res = await fetch(`${API}/locaties/${selectedLocatie}/rustmodus`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...hdrs() },
        body: JSON.stringify({ actief: newState, reden: newState ? 'rustige_dag' : '' })
      });
      if (res.ok) {
        toast.success(newState ? 'Rustmodus geactiveerd' : 'Rustmodus gedeactiveerd');
        fetchData();
      }
    } catch { toast.error('Fout bij wijzigen rustmodus'); }
    finally { setToggling(false); }
  };

  const handleDelete = async () => {
    if (!deleteTaak) return;
    try {
      const res = await fetch(`${API}/rusttaken/${deleteTaak.taakId}`, {
        method: 'DELETE',
        headers: hdrs()
      });
      if (res.ok) {
        toast.success('Taak verwijderd');
        fetchData();
      } else {
        toast.error('Verwijderen mislukt');
      }
    } catch {
      toast.error('Verwijderen mislukt');
    } finally {
      setDeleteTaak(null);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-[#1f2548]" /></div>;
  }

  const taken = data?.taken || [];
  const rustmodusActief = data?.rustmodus?.actief || false;
  const achterstallig = taken.filter(t => t.status === 'achterstallig');
  const aanDeBeurt = taken.filter(t => t.status === 'aan_de_beurt');
  const recent = taken.filter(t => t.status === 'recent');

  const renderSection = (title, items, icon, titleColor) => {
    if (items.length === 0) return null;
    const Icon = icon;
    return (
      <div>
        <h2 className={`font-semibold ${titleColor} mb-2 flex items-center gap-2`}>
          <Icon className="w-4 h-4" /> {title} ({items.length})
        </h2>
        <div className="space-y-2">
          {items.map(t => (
            <TaakCard
              key={t.taakId}
              taak={t}
              onUitvoeren={setUitvoerenTaak}
              onEdit={(tk) => setEditTaak(tk)}
              onDelete={(tk) => setDeleteTaak(tk)}
              magBeheren={magBeheren}
            />
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto px-4 sm:px-0" data-testid="rusttaken-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#1f2548] flex items-center gap-2" data-testid="rusttaken-title">
            <Wrench className="w-6 h-6" /> Rusttaken — {selectedLocatieData?.naam}
          </h1>
          <p className="text-slate-500">Onderhoudstaken voor rustige periodes</p>
        </div>
        {magBeheren && (
          <div className="text-right">
            <Button onClick={() => setShowForm(true)} className="bg-[#1f2548] hover:bg-[#2a3060]" data-testid="nieuwe-rusttaak-btn">
              <Plus className="w-4 h-4 mr-2" /> Nieuwe rusttaak
            </Button>
            <p className="text-xs text-amber-600 mt-1 max-w-[15rem]" data-testid="rusttaken-alle-locaties-let-op">
              Let op: aanpassen, toevoegen en verwijderen geldt voor ALLE locaties
            </p>
          </div>
        )}
      </div>

      {/* Rustmodus toggle */}
      <div className={`rounded-xl border p-4 flex items-center justify-between ${
        rustmodusActief ? 'bg-blue-50 border-blue-200' : 'bg-slate-50 border-slate-200'
      }`} data-testid="rustmodus-toggle">
        <div className="flex items-center gap-3">
          <CloudRain className={`w-6 h-6 ${rustmodusActief ? 'text-blue-600' : 'text-slate-400'}`} />
          <div>
            <p className="font-semibold text-slate-800">
              {rustmodusActief ? 'Rustmodus actief' : 'Rustmodus'}
            </p>
            <p className="text-sm text-slate-500">
              {rustmodusActief
                ? `${(data?.stats?.aan_de_beurt || 0) + (data?.stats?.achterstallig || 0)} rusttaken beschikbaar`
                : 'Activeer bij rustige dag of slecht weer'}
            </p>
          </div>
        </div>
        <Button
          onClick={handleToggleRustmodus}
          disabled={toggling}
          variant={rustmodusActief ? 'outline' : 'default'}
          className={rustmodusActief ? '' : 'bg-[#1f2548] hover:bg-[#2a3060]'}
          data-testid="rustmodus-btn"
        >
          {toggling && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
          {rustmodusActief ? 'Deactiveren' : 'Activeren'}
        </Button>
      </div>

      {renderSection('Achterstallig', achterstallig, XCircle, 'text-red-700')}
      {renderSection('Aan de beurt', aanDeBeurt, AlertTriangle, 'text-amber-700')}
      {renderSection('Recent gedaan', recent, CheckCircle2, 'text-green-700')}

      {taken.length === 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-400" data-testid="rusttaken-empty">
          Geen rusttaken beschikbaar. Klik op "Nieuwe rusttaak" om er een toe te voegen.
        </div>
      )}

      <UitvoerenModal
        taak={uitvoerenTaak}
        locatieId={selectedLocatie}
        onClose={() => setUitvoerenTaak(null)}
        onSaved={fetchData}
      />

      {/* Create/Edit modal */}
      {(showForm || editTaak) && (
        <TaakFormModal
          taak={editTaak}
          onClose={() => { setShowForm(false); setEditTaak(null); }}
          onSaved={fetchData}
        />
      )}

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTaak} onOpenChange={() => setDeleteTaak(null)}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Rusttaak verwijderen?</AlertDialogTitle>
            <AlertDialogDescription>
              Weet je zeker dat je "{deleteTaak?.titel}" wilt verwijderen? Dit kan niet ongedaan worden gemaakt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="delete-confirm-btn">
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Rusttaken;
